import { useState, useRef, useLayoutEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Home,
  UserCircle,
  Briefcase,
  Megaphone,
  TrendingUp,
  Sun,
} from 'lucide-react';
import { useLumisial } from '../context/LumisialContext';
import { TopBar } from './TopBar';
import { FranjaRegularizacion } from './comprobantes/FranjaRegularizacion';
import { useWelcomeTour } from '../hooks/useWelcomeTour';
import { WelcomeTour } from './onboarding/WelcomeTour';

interface LayoutProps {
  children: React.ReactNode;
}

const menuItems = [
  { id: 'home', label: 'Home', icon: Home, path: '/lumisial/home' },
  { id: 'balance', label: 'Balance', icon: TrendingUp, path: '/lumisial/balance' },
  { id: 'miembros', label: 'Miembros', icon: UserCircle, path: '/lumisial/miembros' },
  { id: 'actividades', label: 'Actividades', icon: Briefcase, path: '/lumisial/actividades' },
  { id: 'campanas', label: 'Campañas', icon: Megaphone, path: '/lumisial/campanas' },
];

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const topbarRef = useRef<HTMLElement>(null);
  const [topbarHeight, setTopbarHeight] = useState(57);
  const location = useLocation();
  const { selectedLumisial } = useLumisial();
  const tour = useWelcomeTour();
  const openMobileMenu = () => setSidebarOpen(true);

  const isActive = (path: string) => {
    if (path === '/lumisial/home') {
      return location.pathname === path || location.pathname === '/lumisial';
    }
    return location.pathname.startsWith(path);
  };

  // "L." abreviado, no "Lumisial" completo: medido contra los 38 lumisiales reales
  // (spec, decisión de diseño 8) — el prefijo largo sí trunca en teléfono, este no.
  // El indicador es siempre texto, nunca botón: cambiar de lumisial ya está a dos
  // toques desde el menú del avatar ("Cambiar Lumisial"), y duplicar la acción acá
  // solo agregaba un ícono clickeable sin aportar nada — decisión revertida a pedido
  // explícito del usuario tras usarlo en producción (spec original: decisión 1).
  const etiquetaContexto = `L. ${selectedLumisial?.nombre || 'Lumisial'}`;
  const contexto = { etiqueta: etiquetaContexto, icon: <Sun className="w-4 h-4" /> };

  useLayoutEffect(() => {
    const el = topbarRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => setTopbarHeight(el.offsetHeight));
    observer.observe(el);
    setTopbarHeight(el.offsetHeight);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <TopBar
        ref={topbarRef}
        contexto={contexto}
        onMenuClick={() => setSidebarOpen(!sidebarOpen)}
        showMenuButton={true}
        onRelaunchTour={tour.relaunch}
        helpHintPending={tour.helpHintPending}
        onDismissHelpHint={tour.dismissHelpHint}
        tourActivo={tour.run}
      />

      <div className="flex">
        <aside
          className={`
            fixed lg:sticky left-0
            w-48 bg-white border-r border-gray-200 transition-transform duration-300 z-30 shadow-lg
            ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          `}
          style={{ top: topbarHeight, height: `calc(100vh - ${topbarHeight}px)` }}
        >
          <nav className="p-3 space-y-1">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);

              return (
                <Link
                  key={item.id}
                  to={item.path}
                  data-tour={`menu-${item.id}`}
                  onClick={() => setSidebarOpen(false)}
                  className={`
                    w-full flex items-center gap-2 px-3 py-2.5 rounded-lg transition-all duration-200
                    ${active
                      ? 'bg-blue-600 text-white shadow-md'
                      : 'text-gray-700 hover:bg-gray-100'
                    }
                  `}
                >
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  <span className="font-medium text-sm truncate">{item.label}</span>
                </Link>
              );
            })}
          </nav>
        </aside>

        {sidebarOpen && (
          <button
            type="button"
            aria-label="Cerrar menú"
            className="fixed inset-0 bg-black/50 z-20 lg:hidden"
            onClick={() => setSidebarOpen(false)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setSidebarOpen(false);
            }}
          />
        )}

        <main className="flex-1 min-w-0 p-4 lg:p-8" data-tour="contenido-principal">
          <div className="max-w-7xl mx-auto">
            <FranjaRegularizacion />
            {children}
          </div>
        </main>
      </div>

      <WelcomeTour tour={tour} onOpenMobileMenu={openMobileMenu} />
    </div>
  );
};
